import type { CancelableEventDetail, CancelableEventName } from './types';

/**
 * Emits a cancelable event.
 * @param eventName - The name of the event.
 * @param eventDetail - The detail object for the event.
 * Event will always bubble up and be cancelable.
 * The main use case for this events is to allow for Niftic to cancel actions that are triggered by our components.
 * For instance, when a user clicks on a share button, we want to allow Niftic to cancel the redirection that happens after a channel is selected.
 * @returns Whether the event was cancelled.
 *
 * @example
 * emitCancelableEvent('shareChannelSelectedRedirect', {
 * 	selectedChannel: 'facebook',
 * 	// Actual redirect logic, Niftic might want to continue the redirect after the event is cancelled.
 * 	originalRedirectFn: () => navigate('/somewhere'),
 * });
 */
export function emitCancelableEvent<T extends CancelableEventName>(
	eventName: T,
	eventDetail: CancelableEventDetail<T>,
): boolean {
	const eventConfig: CustomEventInit<CancelableEventDetail<T>> = {
		// Shallow clone the detail to prevent external modifications while preserving functions
		detail: { ...eventDetail },
		bubbles: true,
		cancelable: true,
	};
	const event = new CustomEvent(eventName, eventConfig);

	return window.dispatchEvent(event);
}

export type * from './types';
